import type { FormApi } from '../../form-hooks/useFormApi';
import type { UserFormSubmitFn } from '../../form/UserForm/UserForm';
import type { UserLocalStorage } from '../../types/localStorage';

import { useQueryClient } from '@tanstack/react-query';
import { use, useCallback, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import {
  activateUser,
  deleteAccountRequest,
  postUser,
  resendActivationEmail,
} from '../../lib/flotiq-client';
import { changePasswordRequest } from '../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import {
  generateRedirectUrl,
  getMaskedOrganizationName,
  getTestProps,
} from '../../lib/helpers';

import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import {
  useAllRolesAssigned,
  useAllUserRoles,
  useSpacesList,
  useUser,
} from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useFirstLoading from '../../hooks/useFirstLoading';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import {
  defaultButtonsProps,
  defaultTextRender,
  useModals,
} from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import UserActions from '../../form/UserForm/UserActions/UserActions';
import UserForm from '../../form/UserForm/UserForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

interface ProfileProps {
  /**
   * Indicates if the profile is owned by the current user
   */
  owner?: boolean;
  /**
   * Mode of the form
   */
  mode?: 'edit' | 'add';
}

const testId = 'profile';

const Profile = ({ owner, mode = 'edit' }: ProfileProps) => {
  const { t, i18n } = useTranslation();
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const modal = useModals();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const {
    isAdmin,
    isRoleAdmin,
    userStorage: userDetails,
    baseUserEventData,
  } = use(UserContext);
  const [, setUser] = useLocalStorageState<UserLocalStorage>('cms.user');
  const { setDirty } = use(DirtyHandlerContext);

  const { navigateGrid, gridLink } = useGridNavigate('users', '/users');

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formState, setFormState] = useState<FormApi>();

  const {
    entity: user,
    updateEntity: updateUser,
    deleteEntity: deleteUser,
    reload: reloadUser,
    isLoading,
    errors,
  } = useUser(id || userDetails?.data?.id);

  const organizationHookOptions = useMemo(
    () => ({ pause: !isAdmin }),
    [isAdmin],
  );

  const spacesFilterParam = useMemo(() => {
    if (!isRoleAdmin || !id || mode !== 'edit') return { limit: 1000 };
    return {
      organizationId: user?.organization?.id,
      limit: 1000,
    };
  }, [id, isRoleAdmin, mode, user]);

  const { data: spacesData, isLoading: isLoadingSpacesData } = useSpacesList(
    spacesFilterParam,
    organizationHookOptions,
  );

  const {
    data: allUserRoles,
    isLoading: allUserRolesLoading,
    errors: userRolesErrors,
  } = useAllUserRoles(spacesFilterParam, organizationHookOptions);

  const {
    data: allAssignedRoles,
    isLoading: allAssignedRolesLoading,
    errors: allAssignedRolesErrors,
  } = useAllRolesAssigned(id || userDetails?.data?.id);

  const firstLoading = useFirstLoading(
    !isLoading &&
      !allAssignedRolesLoading &&
      (!isAdmin || (!isLoadingSpacesData && !allUserRolesLoading)),
    mode + id,
  );

  useApiErrorsToast(errors);
  useApiErrorsToast(userRolesErrors);
  useApiErrorsToast(allAssignedRolesErrors);

  const handleDeleteUser = useCallback(async () => {
    setIsDeleting(true);

    const result = await modal({
      title: defaultTextRender.warning(t('Global.Warning')),
      content: t('Users.UserDelete', {
        firstName: user?.firstName,
        lastName: user?.lastName,
      }),
      buttons: [
        {
          ...defaultButtonsProps.delete,
          label: t('Global.Remove'),
          result: true,
        },
        {
          ...defaultButtonsProps.cancel,
          label: t('Global.Cancel'),
          result: false,
        },
      ],
    });

    if (result) {
      try {
        const { body, status, ok } = await deleteUser({ id });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Users.UserDeleteSuccess', { name: user?.email }));

        TagManager.dataLayer({
          dataLayer: {
            event: 'account_leave_org',
            user_id: body.user_id,
            organization_id: body.organization_id,
            organization_name: getMaskedOrganizationName(
              body.organization_name,
            ),
            space_ids: body.space_ids,
            plan_id: undefined,
            plan_name: undefined,
            space_id: undefined,
          },
        });

        TagManager.dataLayer({
          dataLayer: baseUserEventData,
        });

        setDirty(false);
        navigateGrid();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            error.message ? error.message : t('ContentForm.CouldntDelete'),
          );
        }
      }
    }

    setIsDeleting(false);
  }, [
    modal,
    t,
    user,
    deleteUser,
    id,
    baseUserEventData,
    setDirty,
    navigateGrid,
  ]);

  const changeLanguage = useCallback(
    (lang: string) => {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    },
    [i18n],
  );

  const handleUserActionsErrors = useCallback(
    async (callback: () => Promise<null>) => {
      try {
        return await callback();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return { global: t('Form.CommunicationErrorMessage') };
        }

        if (
          error.status === 403 &&
          error.message?.includes('members limit in space')
        )
          toast.error(t('UserForm.TeamMembersLimitExceeded'));
        else if (error.status === 403) toast.error(error.message);
        else
          toast.error(
            error.message ? t('Form.TryAgain') : t('UserForm.UpdatingError'),
          );

        return error.errors;
      }
    },
    [t],
  );

  const onUserChange = useCallback<UserFormSubmitFn>(
    async (values) =>
      await handleUserActionsErrors(async () => {
        const { body, status, ok } = await updateUser(values);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('UserForm.UpdatingSuccess'));
        setIsSaving(false);

        if ((owner || userDetails?.data?.id === user?.id) && values.language) {
          changeLanguage(values.language);
        }

        if (owner && userDetails) {
          const newUserData = { ...userDetails };
          if (values.firstName) newUserData.data.firstName = values.firstName;
          if (values.lastName) newUserData.data.lastName = values.lastName;
          if (values.username) newUserData.data.username = values.username;
          if (values.language) newUserData.data.language = values.language;
          setUser(newUserData);
        }

        queryClient.invalidateQueries({ queryKey: ['users'] });

        return null;
      }),
    [
      handleUserActionsErrors,
      updateUser,
      t,
      owner,
      userDetails,
      user?.id,
      queryClient,
      changeLanguage,
      setUser,
    ],
  );

  const onPasswordRequest = useCallback(
    async (email: string) => {
      const errors = await changePasswordRequest(email, t);
      return errors?.general;
    },
    [t],
  );

  const onDelete = useCallback(
    async (email: string) => {
      try {
        const { body, status, ok } = await deleteAccountRequest(jwt, space, {
          email,
          redirectUri: generateRedirectUrl(
            `/${i18n.language}/delete-account-confirm`,
          ),
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('UserForm.DeleteLinkSuccess'));
        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return t('Form.CommunicationErrorMessage');
        }
        const errorMessage = error.errors?.form
          ? t('Form.FormErrorEmailSent')
          : t('UserForm.DeletingError');
        toast.error(errorMessage);
        return errorMessage;
      }
    },
    [jwt, space, i18n.language, t],
  );

  const handleNoData = useMemo(() => {
    if (!(firstLoading || errors)) return null;
    if (firstLoading) return <Loader size="big" type="spinner-grid" />;
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'no-user')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          <div className="h-full flex items-center justify-center">
            {t('UserForm.FetchError')}
          </div>
        </div>
      </Heading>
    );
  }, [errors, firstLoading, t]);

  const onAddUser = useCallback<UserFormSubmitFn>(
    async (values) => {
      return await handleUserActionsErrors(async () => {
        const { body, status, ok } = await postUser(jwt, undefined, {
          ...values,
          username: values.email,
          ...(getEnv('VITE_SEND_REGISTRATION_EMAIL') !== 'false'
            ? {}
            : { enabled: true, sendMail: false }),
          redirectUri: generateRedirectUrl('/register/activate'),
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        toast.success(t('UserForm.UpdatingSuccess'));

        TagManager.dataLayer({
          dataLayer: {
            event: 'account_join_org',
            user_id: body.user_id,
            organization_id: body.organization_id,
            organization_name: getMaskedOrganizationName(
              body.organization_name,
            ),
            space_ids: body.space_ids,
            space_id: undefined,
            plan_id: undefined,
            plan_name: undefined,
          },
        });

        TagManager.dataLayer({
          dataLayer: baseUserEventData,
        });

        navigateGrid();

        return null;
      });
    },
    [handleUserActionsErrors, jwt, t, baseUserEventData, navigateGrid],
  );

  const onUserActivate = useCallback(
    async (id: string) => {
      try {
        const { body, status, ok } = await activateUser(jwt, undefined, { id });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('UserForm.Activation.Success'));
        reloadUser();

        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return t('Form.CommunicationErrorMessage');
        }

        toast.error(t('UserForm.Activation.Failed'));
        return t('UserForm.Activation.Failed');
      }
    },
    [jwt, reloadUser, t],
  );

  const onActivationEmailResend = useCallback(
    async (id: string) => {
      try {
        const { body, status, ok } = await resendActivationEmail(
          jwt,
          undefined,
          {
            id,
            redirectUri: generateRedirectUrl('/register/activate'),
          },
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('UserForm.ResendActivationEmail.Success'));

        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return t('Form.CommunicationErrorMessage');
        }

        toast.error(t('UserForm.ResendActivationEmail.Failed'));
        return t('UserForm.ResendActivationEmail.Failed');
      }
    },
    [jwt, t],
  );

  const validView = owner || isAdmin;

  const handleNoAccess = useMemo(() => {
    if (!owner && !isAdmin) {
      return (
        <Heading
          level={2}
          additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
          {...getTestProps(testId, 'heading', 'testId')}
        >
          <div className="flex flex-col items-center justify-center text-center">
            <WarningIcon className="text-red w-14 md:w-20 mb-3" />
            {t('Global.NoAccess')}
          </div>
        </Heading>
      );
    }
  }, [isAdmin, owner, t]);

  const pageTitle = useMemo(() => {
    if (owner) return t('Global.AccountSettings');
    return id ? t(`Users.Edit`) : t('Users.Add');
  }, [id, owner, t]);

  return (
    <PageLayout
      page="profile"
      title={pageTitle}
      buttonsDisabled={isSaving || isDeleting}
      breadcrumbs={
        <TopbarBreadcrumbs
          {...(!owner
            ? { parentTitle: t('Global.Users'), parentLink: gridLink }
            : {})}
        />
      }
      buttons={
        owner ? null : (
          <>
            <TopbarCancelButton isDirty={formState?.dirty} link={gridLink} />
            <TopbarSaveButton
              label={mode === 'add' ? t('Global.Invite') : t('Global.Save')}
              isLoading={isSaving}
              form="user-form"
            />
            <TopbarActionMenu>
              {isAdmin && id && userDetails?.data?.id !== id && (
                <TopbarDeleteButton
                  label={t('Global.Remove')}
                  onClick={handleDeleteUser}
                />
              )}
            </TopbarActionMenu>
          </>
        )
      }
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withSidebar}>
        <div
          className={twMerge(
            predefinedLayoutClasses.leftColumnWhite,
            !validView && 'flex justify-center items-center',
            'p-7 md:py-10 md:px-14',
          )}
        >
          {handleNoAccess}

          {validView && (firstLoading || errors) ? (
            <div className="h-full flex items-center justify-center">
              {handleNoData}
            </div>
          ) : (
            validView && (
              <>
                <UserForm
                  mode={mode}
                  user={mode === 'edit' ? user : null}
                  onSubmit={mode === 'edit' ? onUserChange : onAddUser}
                  owner={owner}
                  spacesData={spacesData}
                  userRoles={allUserRoles}
                  assignedRoles={allAssignedRoles}
                  setFormState={setFormState}
                  testId={testId}
                />

                {mode === 'edit' && user && (
                  <>
                    <div
                      className={
                        'h-0 border border-slate-200 dark:border-slate-800 w-full mt-6 md:mt-10 mb-3 md:mb-8'
                      }
                    />
                    <UserActions
                      user={user}
                      onDelete={onDelete}
                      onPasswordRequest={onPasswordRequest}
                      onUserActivate={onUserActivate}
                      onActivationEmailResend={onActivationEmailResend}
                      testId={testId}
                      deletable={owner || userDetails?.data?.id === id}
                      isSectionTutorial={JSON.parse(
                        getEnv('VITE_ENABLE_TUTORIAL') || 'false',
                      )}
                    />
                  </>
                )}
              </>
            )
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Profile;
